/**
 * Apartment management for apartment admins.
 * Lists users in their apartment(s) and allows Approve/Revoke for apartment membership.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { userUpdateProfile } from '../api/userApi';
import { useAlert } from '../contexts/AlertContext';
import './AdminPanel.css';

export default function ApartmentManage() {
  const { showAlert } = useAlert();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [myApartmentIds, setMyApartmentIds] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const [aptSnap, userSnap] = await Promise.all([
          getDocs(collection(db, 'apartments')),
          getDocs(collection(db, 'users'))
        ]);
        const aptList = aptSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const userList = userSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setApartments(aptList);
        setUsers(userList);
        const admins = aptList.filter(a => (a.adminIds || []).includes(currentUser.uid)).map(a => a.id);
        setMyApartmentIds(admins);
        if (admins.length === 0) {
          navigate('/dashboard');
          return;
        }
      } catch (e) {
        console.error(e);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUser, navigate]);

  const members = users.filter(u => u.apartmentId && myApartmentIds.includes(u.apartmentId));

  async function toggleApproved(userId, currentApproved) {
    try {
      await userUpdateProfile(userId, currentUser.uid, { apartmentApproved: !currentApproved });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, apartmentApproved: !currentApproved } : u));
    } catch (err) {
      showAlert('Error: ' + (err.message || err), { variant: 'error' });
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const apartmentNames = Object.fromEntries(apartments.map(a => [a.id, a.name]));

  return (
    <div className="admin-panel-container">
      <div className="admin-header">
        <h1>Manage my apartment(s)</h1>
        <button onClick={() => navigate('/dashboard')} className="btn-secondary">Back to Dashboard</button>
      </div>
      <div className="admin-content">
        <div className="admin-section">
          <p style={{ marginBottom: '16px', color: '#666' }}>
            Approve or revoke users for your apartment. Approved users can see and join apartment-specific tournaments.
          </p>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Apartment</th>
                  <th>Flat</th>
                  <th>Approved</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr><td colSpan={6}>No users in your apartment(s) yet.</td></tr>
                ) : (
                  members.map(user => {
                    const approved = user.apartmentApproved === true || user.apartmentApproved === 'true';
                    const name = user.displayName || user.email || '—';
                    return (
                      <tr key={user.id}>
                        <td>{name}</td>
                        <td>{user.email || '—'}</td>
                        <td>{apartmentNames[user.apartmentId] || user.apartmentName || '—'}</td>
                        <td>{user.flatNumber || '—'}</td>
                        <td>{approved ? '✓' : '—'}</td>
                        <td>
                          <button
                            onClick={() => toggleApproved(user.id, approved)}
                            className={`btn-small ${approved ? 'btn-warning' : 'btn-success'}`}
                          >
                            {approved ? 'Revoke' : 'Approve'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
